// Laadt de cross-tenant onderzoeksdataset voor de meetstandaard-onderzoekers
// (superTenant). Haalt de effect-catalogus uit de meetstandaard-api (incl.
// sectors), en per MSI-effect de item-scores van alle organisaties via de
// super-admin-gated Cloud Function `msiResearchData`.
//
// De catalogus komt uit BEIDE api-pipelines (`fetchStandaardCatalogs`): legacy
// `/database` én de versioned documenten. Energiearmoede komt nooit in
// `/database`, dus alleen daaruit lezen liet die effecten stil uit de
// onderzoeksdataset vallen — met een keurige 200 als bewijs dat alles goed ging.
//
// AI-CONTEXT: See docs/pitfalls.md#api-dual-pipeline-legacy-only-consumer
//
// De zware CF-calls lopen per effect (zelfde patroon als de tenant-
// correlatiematrix); één mislukte call laat de rest van de dataset bruikbaar.
//
// Returnt:
//   {
//     dataset,        // buildMsiResearchDataset-output (breed + kolommen)
//     effects,        // [{ msiId, name }] — effecten in scope (na sectorfilter)
//     effectErrors,   // { [msiId]: message } — per-effect fout, zichtbaar in de UI
//     loading, error, // laad-status / catalogus-fout
//   }

import { useEffect, useState } from 'react'
import firebase from '../firebase/compat'
import buildMsiResearchDataset from '../helpers/analysisHelpers/buildMsiResearchDataset'
import fetchStandaardCatalogs from '../helpers/meetstandaard/fetchStandaardCatalogs'
import mergeStandaardCatalogs from '../helpers/meetstandaard/mergeStandaardCatalogs'

// Module-level cache per sector: de analyse-pagina en de dataweergave mounten
// de hook elk apart, en de doorklik + terugknop zouden anders telkens de hele
// dataset opnieuw laden (één zware CF-call per effect). Sessie-lange
// staleness is prima voor een onderzoeksview; harde refresh leegt de cache.
/** @type {Map<string, Promise<{catalog: Array<{msiId: string, name: string}>, errors: Object<string, string>, rows: object[]}>>} */
const loadCache = new Map()

const loadResearchData = async (sector) => {

    // 1. Effect-catalogus (naam + sectors per msiId) uit beide api-pipelines.
    // Een fout in één pipeline laat de hele load klappen: half laden zou een
    // dataset opleveren waarin een sector ontbreekt zonder dat iemand het ziet.
    const { legacy, documents } = await fetchStandaardCatalogs()
    const database = mergeStandaardCatalogs(legacy, documents)

    const catalog = []
    database.forEach((category) => {
        const categoryEffects = Array.isArray(category?.effects) ? category.effects : []
        categoryEffects.forEach((effect) => {
            if (effect?.id === undefined || effect?.id === null) return
            const sectors = Array.isArray(effect.sectors) ? effect.sectors : []
            if (sector && !sectors.includes(sector)) return
            catalog.push({ msiId: String(effect.id), name: effect.name || String(effect.id) })
        })
    })

    // 2. Per effect de cross-tenant rows — fouten per effect vangen we
    // apart zodat de rest van de dataset bruikbaar blijft (de fout
    // blijft zichtbaar in de UI, geen silent catch).
    const callable = firebase.app().functions('europe-west1').httpsCallable('msiResearchData')
    const errors = {}
    const allRows = []

    await Promise.all(catalog.map(async ({ msiId, name }) => {
        try {
            const result = await callable({ msiId })
            const rows = Array.isArray(result?.data?.rows) ? result.data.rows : []
            rows.forEach((row) => allRows.push({ ...row, msiId, effectName: name }))
        } catch (err) {
            console.error(`msiResearchData failed for effect ${msiId}:`, err)
            errors[msiId] = err?.message || 'Onbekende fout'
        }
    }))

    return { catalog, errors, rows: allRows }
}

/**
 * @param {string} sector - Sector-slug om op te filteren; '' = hele meetstandaard
 */
const useMsiResearchDataset = (sector) => {

    const [dataset, setDataset] = useState(/** @type {ReturnType<typeof buildMsiResearchDataset> | null} */ (null))
    const [effects, setEffects] = useState(/** @type {Array<{msiId: string, name: string}>} */ ([]))
    const [effectErrors, setEffectErrors] = useState(/** @type {Object<string, string>} */ ({}))
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    useEffect(() => {

        let cancelled = false
        setLoading(true)
        setError('')
        setDataset(null)
        setEffectErrors({})

        if (!loadCache.has(sector)) {
            loadCache.set(sector, loadResearchData(sector))
        }

        loadCache.get(sector)
            .then(({ catalog, errors, rows }) => {
                if (cancelled) return
                setEffects(catalog)
                setEffectErrors(errors)
                setDataset(buildMsiResearchDataset(rows))
                setLoading(false)
            })
            .catch((err) => {
                // Catalogus-fout: niet cachen, anders blijft de pagina kapot
                // tot een harde refresh
                loadCache.delete(sector)
                console.error('useMsiResearchDataset load failed:', err)
                if (cancelled) return
                setError(err?.message || 'De onderzoeksdata kon niet worden geladen.')
                setLoading(false)
            })

        return () => { cancelled = true }

    }, [sector])

    return { dataset, effects, effectErrors, loading, error }
}

/** Test seam — de module-cache zou anders tussen testgevallen blijven staan. */
const __resetResearchDatasetCache = () => loadCache.clear()

export default useMsiResearchDataset
export { __resetResearchDatasetCache }
